import React, { useEffect } from 'react';
import {
} from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectSignUp, setMonthly, SignUP,
} from '../features/signup/signupSlice';
import { Plan } from '../models/plans';
import { Router } from '../models/routers';
import { selectAddress } from '../features/address/addressSlice';
import {
  selectBookingState,
} from '../features/bookings/bookingsSlice';
import {
  selectDiscount,
  discountWording,
  Discount,
} from '../features/discount/discountSlice';
import {
  pineAppleDay, pineAppleTime,
} from '../lib/utils';
import { afterDiscountRate, revertsToRRP } from '../lib/rrp';
import { selectPromoPricing } from '../features/featureFlags/featureFlagsSlice';
import { isCommunityFibrePlan } from '../features/finale/orderAPI';

export const GetMonthlyCost = (plan: Plan) => {
  const cost = parseFloat(plan.cost);
  return cost;
};

export const getDiscountPlanCost = (
  initialCost: number,
  discount?: Discount,
): number => {
  if (discount?.starting_period !== 0) return initialCost;

  if (discount?.percentage) {
    return (initialCost * (100 - parseFloat(discount.percentage))) / 100;
  }

  if (discount?.dollar_amount) {
    return (initialCost - (discount.dollar_amount / 100));
  }

  return initialCost;
};

export const GetTotalCost = (
  plan: Plan,
  router: Router,
  greenfield: boolean,
  staticIp: boolean,
  discount?: Discount,
) => {
  const planCost = getDiscountPlanCost(GetMonthlyCost(plan), discount);
  let total = 0;
  if (planCost) {
    total += planCost;
  }
  if (router.cost) {
    total += parseFloat(router.cost);
  }
  if (greenfield) {
    total += 300;
  }

  if (staticIp) {
    total += 9;
  }
  return total;
};

export const summaryOpen = (signUpForm: SignUP) => !!(
  signUpForm.plan.id || signUpForm.router.id || signUpForm.addOns.includes("static-ip")
);

export const planBuilt = (signUpForm: SignUP) => !!(
  signUpForm.plan.id && signUpForm.router.id
);

const OrderSummary = () => {
  const signUpForm = useAppSelector(selectSignUp);
  const address = useAppSelector(selectAddress);
  const bookingsState = useAppSelector(selectBookingState);
  const discount = useAppSelector(selectDiscount);
  const promoPricing = useAppSelector(selectPromoPricing);

  const { plan, router } = signUpForm;

  // A DGtek order books an installer slot. A Community Fibre order asks
  // only for a day, so it has no booking.
  const preferredDate = isCommunityFibrePlan(plan)
    ? signUpForm.preferredDate
    : undefined;
  const installationChosen = Boolean(
    bookingsState.wantedBookingId.start || preferredDate,
  );

  if (!summaryOpen(signUpForm)) {
    return null;
  }

  const totalCost = GetTotalCost(
    plan,
    router,
    address.buildingCategory === 'greenfield',
    signUpForm.addOns.includes("static-ip"),
    discount,
  );
  const monthlyCost = GetMonthlyCost(plan);
  const dispatch = useAppDispatch();
  const discountedMonthlyCost = getDiscountPlanCost(GetMonthlyCost(plan), discount);

  useEffect(() => {
    dispatch(setMonthly(discountedMonthlyCost));
  }, [discountedMonthlyCost]);

  return (
    <div
      style={{ borderTop: '2px solid' }}
      className="
          px-2
          px-md-0
          py-2
          border-primary"
    >
      <h4>Your Order Summary</h4>
      { address.formatted_address ? (
        <>
          <h6 className="text-uppercase">Address</h6>
          <ul className="list-unstyled">
            <li>
              <strong className="text-primary">
                { (signUpForm.arrivy.customer_address_line_1 || address.unit)
                  ? `Unit ${signUpForm.arrivy.customer_address_line_1 || address.unit}, `
                  : '' }
                { address.formatted_address }
              </strong>
            </li>
          </ul>
        </>
      ) : null }
      <h6>PLAN DETAILS</h6>
      <ul className="list-unstyled">
        { plan.id ? (
          <li>
            Plan:
            {' '}
            <strong className="text-primary">
              { plan.name }
              {' '}
              { plan.download ?? plan.speed }
              {' '}
              /
              {' '}
              { plan.upload ?? plan.speed }
              {' '}
              Mbps
            </strong>
            {' '}
            &mdash;
            {' '}
            <strong>
              { discount?.period !== 0
              && (monthlyCost !== discountedMonthlyCost)
                ? (
                  <span>
                    <span className="strikethrough">
                      $
                      { monthlyCost }
                    </span>
                    {' '}
                    $
                    { discountedMonthlyCost }
                  </span>
                )
                : (
                  <span>
                    $
                    { GetMonthlyCost(plan) }
                  </span>
                )}
            </strong>
            {' '}
            {signUpForm?.coupon?.coupon_id?.includes("3MF") ? `for the first three month then $${monthlyCost} for the following three months.` : "per month"}
            {revertsToRRP(plan, monthlyCost, promoPricing) && (
              <>
                <br />
                <small>{`After the promotional period, ${afterDiscountRate(plan, promoPricing)}.`}</small>
              </>
            )}
            { discountWording(discount) !== ""
              ? (
                <>
                  <br />
                  <small
                    className="fw-bold
                    d-inline-block
                    bg-secondary
                    my-1
                    py-1
                    px-2
                    text-center
                    rounded-pill"
                  >
                    {signUpForm?.coupon?.coupon_id === "PNBF3MF" ? (
                      <span>
                        <b>3 Months FREE</b>
                        {' '}
                        – Congrats!
                        {' '}
                        {' '}
                        Your first 3
                        <br />
                        {' '}
                        Months are on US!
                      </span>
                    ) : discountWording(discount)}
                  </small>
                </>
              ) : null}
          </li>
        ) : null }
        { signUpForm.addOns.includes("static-ip") ? (
          <li>
            Addons:
            {' '}
            <strong className="text-primary">Static IP Address</strong>
            {' – '}
            <strong>
              $9
              {' '}
            </strong>
            per month
          </li>
        ) : null }
        { router.id ? (
          <li>
            Modem:
            {' '}
            <strong className="text-primary">{ router.name }</strong>
            { router.cost === '0' ? null : (
              <>
                {' – '}
                <strong>
                  $
                  {router.cost}
                </strong>
                {` ${router.description2}`}
              </>
            ) }
          </li>
        ) : null }
        { address.buildingCategory === 'greenfield'
          && (
          <li>
            Additional Charges:
            {' '}
            <strong className="text-primary">New Development</strong>
            {' – '}
            <strong>
              $300
              {' '}
            </strong>
            one off charge
          </li>
          )}
        { planBuilt(signUpForm)
          ? (
            <li>
              Total cost on activation:
              {' '}
              <strong>
                $
                {totalCost}
              </strong>
            </li>
          ) : null }
      </ul>
      { signUpForm.arrivy.customer_email ? (
        <>
          <h6 className="text-uppercase">Primary contact</h6>
          <ul className="list-unstyled">
            { signUpForm.arrivy.customer_first_name ? (
              <li>
                First name:
                {' '}
                <strong
                  className="text-primary"
                >
                  { signUpForm.arrivy.customer_first_name }
                </strong>
              </li>
            ) : null }
            { signUpForm.arrivy.customer_last_name ? (
              <li>
                Last name:
                {' '}
                <strong className="text-primary">
                  { signUpForm.arrivy.customer_last_name }
                </strong>
              </li>
            ) : null }
            { signUpForm.arrivy.customer_mobile_number ? (
              <li>
                Mobile:
                {' '}
                <strong className="text-primary">
                  { signUpForm.arrivy.customer_mobile_number }
                </strong>
              </li>
            ) : null }
            { signUpForm.arrivy.customer_email ? (
              <li>
                Email:
                {' '}
                <strong className="text-primary">
                  { signUpForm.arrivy.customer_email }
                </strong>
              </li>
            ) : null }
          </ul>
        </>
      ) : null }
      { installationChosen ? (
        <>
          <h6 className="text-uppercase">Installation Address</h6>
          <ul className="list-unstyled">
            { signUpForm.arrivy.customer_address_line_1 ? (
              <li>
                Unit:
                {' '}
                <strong
                  className="text-primary"
                >
                  { signUpForm.arrivy.customer_address_line_1 }
                </strong>
              </li>
            ) : null }
            { signUpForm.arrivy.customer_address_line_2 ? (
              <li>
                Street number:
                {' '}
                <strong
                  className="text-primary"
                >
                  {signUpForm.arrivy.customer_address_line_2 }
                </strong>
              </li>
            ) : null }
            { address.suburb ? (
              <li>
                Suburb:
                {' '}
                <strong
                  className="text-primary"
                >
                  { address.suburb }
                </strong>
              </li>
            ) : null }
            { address.postcode ? (
              <li>
                Postcode:
                {' '}
                <strong
                  className="text-primary"
                >
                  { address.postcode }
                </strong>
              </li>
            ) : null }
            { address.state ? (
              <li>
                State:
                {' '}
                <strong
                  className="text-primary"
                >
                  { address.state }
                </strong>
              </li>
            ) : null }
          </ul>
        </>
      ) : null }
      { bookingsState.wantedBookingId.start ? (
        <>
          <h6 className="text-uppercase">Installation Time</h6>
          <ul className="list-unstyled">
            { bookingsState.wantedBookingId.start ? (
              <li>
                Day:
                {' '}
                <strong
                  className="text-primary"
                >
                  { pineAppleDay(new Date(bookingsState.wantedBookingId.start)) }
                </strong>
              </li>
            ) : null }
            { bookingsState.wantedBookingId.start ? (
              <li>
                Time:
                {' '}
                <strong
                  className="text-primary"
                >
                  { pineAppleTime(new Date(bookingsState.wantedBookingId.start), address.state) }
                  {' - '}
                  { pineAppleTime(new Date(bookingsState.wantedBookingId.end), address.state) }
                </strong>
              </li>
            ) : null }
          </ul>
        </>
      ) : null }
      { preferredDate ? (
        <>
          <h6 className="text-uppercase">Preferred installation date</h6>
          <ul className="list-unstyled">
            <li>
              Day:
              {' '}
              <strong className="text-primary">
                { pineAppleDay(new Date(`${preferredDate}T00:00:00`)) }
              </strong>
            </li>
          </ul>
        </>
      ) : null }
    </div>
  );
};

export default OrderSummary;
